import React, { memo } from 'react';

import {
  Tag,
  Typography,
} from 'antd';

import { TagOutlined } from '@ant-design/icons';

import CustomReadMoreContent
  from '../../../../components/CustomReadMoreText/CustomReadMoreContent';
import ProfileItem from '../../../../components/ProfileItem/ProfileItem';
import { useTheme } from '../../../../context/ThemeProvider';
import { useTQConnectConfig } from '../../../../hooks/queries/tqConnectQueries';
import {
  DEFAULT_AVATAR,
  DELETED_USER,
} from '../../../../shared/data';
import { downloadFileWithProgress } from '../../../../shared/fileUtils';
import { useAppNavigate } from '../../../../shared/hooks';
import {
  getTQServiceRequestReviewRating,
} from '../../../../shared/tqConnectUtils';
import {
  getItemDisplayName,
  getReadableDateAndTimeFormat,
} from '../../../../shared/utils';
import { ROUTES } from '../../../../types/routes';
import { ITQProfileReview } from '../../../../types/tqConnectTypes/user';
import AttachmentItem
  from '../../../TQConnect/components/TQServiceRequestResponseBottpmSheet/AttachmentItem';

interface Props {
  review: ITQProfileReview;
}

const ReviewCard: React.FC<Props> = ({ review }) => {
  const navigate = useAppNavigate();

  const { colors: COLORS } = useTheme();

  const { data: tqConnectConfigResult } = useTQConnectConfig();

  const {
    rating,
    feedback,
    attachments,
    date,
    givenBy,
    respondedTo: serviceRequest,
  } = review;

  const ratingCategory = getTQServiceRequestReviewRating(
    rating,
    tqConnectConfigResult?.tqConnectConfig,
  );

  const goToUserProfile = (userId?: string | null) => {
    if (!userId) return;

    navigate(ROUTES.USER_PROFILE, { userId });
  };

  return (
    <div className="review-card">
      <div className="card__header">
        <ProfileItem
          className="card__header__content"
          avatar={givenBy?.profilePicUrl || DEFAULT_AVATAR}
          avatarSize={40}
          title={givenBy?.name || DELETED_USER}
          subtitle={
            <div className="content__subtitle">
              <Typography.Text
                className="content__subtitle__rating"
                type="secondary">
                <span className="rating__logo">{ratingCategory?.logo}</span>{' '}
                <span className="rating__name">{ratingCategory?.name}</span>
              </Typography.Text>
              &nbsp;•&nbsp;
              <Typography.Text
                className="content__date"
                type="secondary"
                style={{ fontWeight: 400 }}>
                {getReadableDateAndTimeFormat(date)}
              </Typography.Text>
            </div>
          }
          subtitleStyle={{ width: '100%', cursor: 'auto' }}
          onClick={() => goToUserProfile(givenBy?._id)}
        />
      </div>

      <CustomReadMoreContent lines={3} className="card__caption">
        {feedback}
      </CustomReadMoreContent>

      {attachments?.length ? (
        <div className="card__attachments">
          {attachments.map((attachment) => (
            <AttachmentItem
              key={attachment.url}
              attachment={attachment}
              onDownload={({ url, name }) =>
                downloadFileWithProgress(url, name)
              }
            />
          ))}
        </div>
      ) : null}

      {serviceRequest ? (
        <Tag
          typeof="processing"
          className="card__tag"
          icon={
            <TagOutlined
              color="currentColor"
              style={{
                rotate: '45deg',
                fontSize: 16,
                color: COLORS.PRIMARY,
              }}
            />
          }
          style={{
            color: COLORS.PRIMARY,
            backgroundColor: COLORS.PRIMARY_LIGHT,
            textTransform: 'unset',
          }}>
          {getItemDisplayName(serviceRequest.title)}
        </Tag>
      ) : null}
    </div>
  );
};

export default memo(ReviewCard);
